import { useCallback, useState, useEffect } from 'react';
import { Form, Row, Table } from 'antd';
import { AButton, AModal, ATitle } from '../../../../components';
import {
  IPaymentMethod,
  ITreatment,
  IPaymentItemInsert,
} from '../../../../shared/interfaces';
import { currency } from '../../../../shared/utils/functions';
import InsertPayment from '../../../../components/InsertPayment';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { formatDate } from '../../../../shared/utils/format-date';
import { useToast } from '../../../../hooks/toast';
import { useNavigate } from 'react-router-dom';
import { postTreatmentPayment } from '../../../../http/treatmentsPayments';
import { IRetailSaleProduct } from '../../../../components/TreatmentRetailProductsModal';

interface IConfirmTreatment {
  open: boolean;
  close: () => void;
  treatment: ITreatment | null;
  paymentsMethods: IPaymentMethod[];
  setApproval: React.Dispatch<React.SetStateAction<boolean>> | null;
  retailProducts?: IRetailSaleProduct[];
  treatmentTotal?: number;
  totalAmount?: number;
  treatment_id?: string;
}

const ConfirmTreatment: React.FC<IConfirmTreatment> = ({
  open,
  close,
  treatment,
  paymentsMethods,
  setApproval,
  retailProducts = [],
  treatmentTotal,
  totalAmount,
  treatment_id: treatmentIdProp,
}) => {
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [paymentsItemsInsert, setPaymentsItemsInsert] = useState<
    IPaymentItemInsert[]
  >([]);
  const [paymentItemInsert, setPaymentItemInsert] =
    useState<IPaymentItemInsert | null>(null);
  const [insertModal, setInsertModal] = useState<boolean>(false);
  const retailTotal = retailProducts.reduce(
    (sum, item) => sum + item.total_price,
    0,
  );
  const effectiveTreatmentTotal =
    treatmentTotal ??
    Math.max(
      0,
      Number(treatment?.total || 0) - Number(treatment?.discount || 0),
    );
  const effectiveTotal = totalAmount ?? effectiveTreatmentTotal + retailTotal;
  const [remaining, setRemaining] = useState<number>(effectiveTotal);

  const resolveTreatmentId = (): string | null => {
    const candidates = [
      treatmentIdProp,
      (treatment as any)?.id,
      (treatment as any)?.t_id,
      (treatment as any)?.treatment_id,
      (treatment as any)?.treatment?.id,
      (treatment as any)?.treatment?.t_id,
      (treatment as any)?.data?.id,
      (treatment as any)?.data?.treatment_id,
    ];
    for (const c of candidates) {
      const str = c == null ? '' : String(c);
      if (str.trim() !== '' && str.trim().toLowerCase() !== 'new') return str;
    }
    return null;
  };

  const calculateRemaining = useCallback(() => {
    const totalInsert = paymentsItemsInsert.reduce(
      (acc, current) => acc + current.value,
      0,
    );
    const remain = effectiveTotal - totalInsert;
    setRemaining(remain);
  }, [paymentsItemsInsert, effectiveTotal]);

  const renderModalInsertItem = () => {
    if (insertModal)
      return (
        <InsertPayment
          open={insertModal}
          close={() => setInsertModal(false)}
          paymentItemInsert={paymentItemInsert}
          setPaymentsItemsInsert={setPaymentsItemsInsert}
          paymentsMethods={paymentsMethods}
          remaining={remaining}
        />
      );
  };

  const handleRemove = useCallback((item: IPaymentItemInsert) => {
    setPaymentsItemsInsert((state: IPaymentItemInsert[]) =>
      state.filter((e) => e.id !== item.id),
    );
  }, []);

  const handleCreateEdit = useCallback((item: IPaymentItemInsert | null) => {
    setPaymentItemInsert(item);
    setInsertModal(true);
  }, []);

  const handleApproval = useCallback(async () => {
    const resolvedId = resolveTreatmentId();
    try {
      if (!resolvedId) {
        throw new Error('Tratamento sem ID válido para aprovação');
      }
      if (Math.abs(remaining) > 0.05) {
        throw new Error('Pagamento incompleto ou valor restante excedido');
      }

      const data = {
        treatment_id: String(resolvedId),
        items: paymentsItemsInsert.map((i) => ({
          ...i,
          payment_id: String(i.payment_id ?? ''),
        })),
        retail_items: retailProducts.map((p) => ({
          ...p,
          product_id: String(p.product_id ?? ''),
        })),
      };

      await postTreatmentPayment(data);
      setApproval?.(true);
      addToast({
        type: 'success',
        title: 'Aprovado',
        description: 'Aprovado com sucesso.',
      });
      setTimeout(function () {
        navigate(-1);
      }, 1000);
    } catch (e: any) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: e.message,
      });
    }
  }, [
    paymentsItemsInsert,
    remaining,
    retailProducts,
    treatment,
    treatmentIdProp,
  ]);

  useEffect(() => {
    setRemaining(effectiveTotal);
    setPaymentsItemsInsert([]);
  }, [effectiveTotal, open]);

  useEffect(() => {
    calculateRemaining();
  }, [paymentsItemsInsert, calculateRemaining]);

  return (
    <>
      <AModal
        open={open}
        close={close}
        title="Aprovar Tratamento"
        okText={'Aprovar'}
        onOk={handleApproval}
        width={760}
      >
        <Row
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: 12,
          }}
        >
          <div>
            <div
              style={{ textAlign: 'center', color: '#595959', fontSize: 13 }}
            >
              Tratamento: {currency(effectiveTreatmentTotal)}
            </div>
            {retailTotal > 0 && (
              <div
                style={{ textAlign: 'center', color: '#595959', fontSize: 13 }}
              >
                Venda avulsa: {currency(retailTotal)}
              </div>
            )}
            <div
              style={{
                textAlign: 'center',
                color: '#1677ff',
                fontWeight: 600,
                fontSize: 14,
              }}
            >
              Total do fechamento: {currency(effectiveTotal)}
            </div>
          </div>
        </Row>
        <Row style={{ display: 'flex', justifyContent: 'center' }}>
          <ATitle level={5}>Valor Restante: {currency(remaining)}</ATitle>
        </Row>
        <Row style={{ display: 'flex', justifyContent: 'end' }}>
          <AButton type="primary" onClick={() => handleCreateEdit(null)}>
            Inserir pagamento
          </AButton>
        </Row>
        <Form layout="vertical">
          <Table
            rowKey={(record) =>
              (record as any)?.id ??
              (record as any)?.payment_id ??
              (record as any)?.installment_key ??
              `row-${String((record as any)?.installment ?? Math.random())}-${Date.now()}`
            }
            style={{ width: '100%', marginTop: 10 }}
            scroll={{ y: 350 }}
            columns={[
              {
                title: '#',
                width: 30,
                render: (value, record, index) => <>{index + 1}</>,
              },
              {
                title: 'Pagamento',
                dataIndex: 'payment_id',
                render: (value: string) => {
                  const descriptionPayment = paymentsMethods.find(
                    (x) => x.id === value,
                  )?.description;
                  return <>{descriptionPayment}</>;
                },
                width: 180,
              },
              {
                title: 'Parcelas',
                dataIndex: 'installment',
                width: 110,
              },
              {
                title: 'Vencimento',
                dataIndex: 'due_date',
                width: 110,
                render: (value: string) => <>{formatDate(value)}</>,
              },
              {
                title: 'Valor',
                width: 130,
                dataIndex: 'value',
                render: (value: number) => <>{currency(value)}</>,
              },
              {
                title: 'Opções',
                width: 120,
                render: (cell: IPaymentItemInsert) => (
                  <Row>
                    <AButton
                      type="primary"
                      danger
                      onClick={() => handleRemove(cell)}
                      icon={<DeleteOutlined />}
                      title="Excluir"
                    />
                    <AButton
                      type="primary"
                      onClick={() => handleCreateEdit(cell)}
                      icon={<EditOutlined />}
                      title="Editar"
                    />
                  </Row>
                ),
              },
            ]}
            dataSource={paymentsItemsInsert}
            pagination={false}
            bordered
          />
        </Form>
      </AModal>
      {renderModalInsertItem()}
    </>
  );
};

export default ConfirmTreatment;
